import { ImageFromServer } from '@/components/images/ImageFromServer';
import { CompletionDataExecutionTypeEnum, CompletedExercise, TrainingHistoryEntry } from '@/generated';
import { useAllExercisesQuery } from '@/hooks/query-hooks';
import { Add, DeleteOutline, Timer } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

type EditTrainingHistoryDialogProps = {
  entry?: TrainingHistoryEntry;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (entry: TrainingHistoryEntry) => void;
};

type ExerciseLocation = {
  roundIndex: number;
  exerciseIndex: number;
};

const cloneEntry = (entry: TrainingHistoryEntry) => JSON.parse(JSON.stringify(entry)) as TrainingHistoryEntry;
const nonNegativeNumber = (value: string) => Math.max(0, Number(value) || 0);

export const EditTrainingHistoryDialog = (props: EditTrainingHistoryDialogProps) => {
  const [draft, setDraft] = useState<TrainingHistoryEntry>();
  const [selectedExercise, setSelectedExercise] = useState<ExerciseLocation>();
  const { exercises } = useAllExercisesQuery(props.open);
  useEffect(() => {
    if (!props.open || !props.entry) return;
    setDraft(cloneEntry(props.entry));
    setSelectedExercise(undefined);
  }, [props.entry, props.open]);

  const updateExercise = (location: ExerciseLocation, updatedExercise: CompletedExercise) => {
    setDraft((current) => {
      if (!current) return current;
      const rounds = current.completedTraining.rounds.map((round, roundIndex) => ({
        ...round,
        exercises: round.exercises.map((exercise, exerciseIndex) => (roundIndex === location.roundIndex && exerciseIndex === location.exerciseIndex ? updatedExercise : exercise)),
      }));
      return { ...current, completedTraining: { ...current.completedTraining, rounds } };
    });
  };

  const completionMinutes = Math.floor((draft?.completionTime ?? 0) / 60000);
  const completionSeconds = Math.floor(((draft?.completionTime ?? 0) % 60000) / 1000);

  const updateCompletionTime = (minutes: number, seconds: number) => {
    setDraft((current) => (current ? { ...current, completionTime: (Math.max(0, minutes) * 60 + Math.max(0, seconds)) * 1000 } : current));
  };

  return (
    <Dialog open={props.open} onClose={props.saving ? undefined : props.onClose} fullWidth maxWidth="xl">
      <DialogTitle>Edit training</DialogTitle>
      <DialogContent dividers>
        {draft && (
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Timer color="primary" />
                <Typography variant="h6">Total training duration</Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Minutes"
                    type="number"
                    fullWidth
                    slotProps={{ htmlInput: { min: 0 } }}
                    value={completionMinutes}
                    onChange={(event) => updateCompletionTime(nonNegativeNumber(event.target.value), completionSeconds)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Seconds"
                    type="number"
                    fullWidth
                    slotProps={{ htmlInput: { min: 0, max: 59 } }}
                    value={completionSeconds}
                    onChange={(event) => updateCompletionTime(completionMinutes, Math.min(59, nonNegativeNumber(event.target.value)))}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.max(1, draft.completedTraining.rounds.length)}, minmax(280px, 1fr))`,
                gap: 2,
                overflowX: 'auto',
                pb: 1,
              }}
            >
              {draft.completedTraining.rounds.map((round, roundIndex) => (
                <Stack key={roundIndex} spacing={1.5} sx={{ minWidth: 0 }}>
                  <Typography variant="h6">Round {roundIndex + 1}</Typography>
                  {round.exercises
                    .filter((exercise): exercise is CompletedExercise => exercise !== null)
                    .map((exercise) => {
                      const exerciseIndex = round.exercises.indexOf(exercise);
                      const location = { roundIndex, exerciseIndex };
                      const isSelected = selectedExercise?.roundIndex === roundIndex && selectedExercise.exerciseIndex === exerciseIndex;
                      const exerciseDetails = exercises?.find((item) => item.id === exercise.exerciseId);

                      return (
                        <Card key={`${exercise.exerciseId}-${exerciseIndex}`} variant="outlined">
                          <CardActionArea onClick={() => setSelectedExercise(isSelected ? undefined : location)} aria-expanded={isSelected}>
                            <CardContent>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <ImageFromServer
                                  sx={{ width: 72, height: 72, flexShrink: 0, objectFit: 'contain' }}
                                  thumbnail
                                  alt={exerciseDetails?.name ?? 'Exercise'}
                                  id={exerciseDetails?.imageId}
                                />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                                    {exerciseDetails?.name ?? exercise.exerciseId}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {executionTypeLabel(exercise.completionData.executionType)} · Click to edit
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </CardActionArea>
                          {isSelected && (
                            <Box sx={{ px: 2, pb: 2 }}>
                              <Divider sx={{ mb: 2 }} />
                              <CompletedExerciseEditor exercise={exercise} onChange={(updatedExercise) => updateExercise(location, updatedExercise)} />
                            </Box>
                          )}
                        </Card>
                      );
                    })}
                </Stack>
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={props.saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => draft && props.onSave(draft)} disabled={!draft || props.saving}>
          {props.saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CompletedExerciseEditor = (props: { exercise: CompletedExercise; onChange: (exercise: CompletedExercise) => void }) => {
  const completionData = props.exercise.completionData;
  const isPerSide = completionData.trackPerSide ?? props.exercise.config.trackPerSide ?? false;
  const updateCompletionData = (changes: Partial<typeof completionData>) => props.onChange({ ...props.exercise, completionData: { ...completionData, ...changes } });

  if (completionData.executionType === CompletionDataExecutionTypeEnum.Intervals) {
    const intervals = completionData.completedIntervals ?? [];
    return (
      <Stack spacing={1.5}>
        {intervals.map((interval, intervalIndex) => (
          <Stack key={intervalIndex} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              label={`Interval ${intervalIndex + 1} (seconds)`}
              type="number"
              fullWidth
              slotProps={{ htmlInput: { min: 0 } }}
              value={interval.completedPrimaryTime}
              onChange={(event) =>
                updateCompletionData({
                  completedIntervals: intervals.map((item, index) => (index === intervalIndex ? { ...item, completedPrimaryTime: nonNegativeNumber(event.target.value) } : item)),
                })
              }
            />
            {isPerSide && (
              <TextField
                label={`Second side ${intervalIndex + 1} (seconds)`}
                type="number"
                fullWidth
                slotProps={{ htmlInput: { min: 0 } }}
                value={interval.completedSecondaryTime ?? 0}
                onChange={(event) =>
                  updateCompletionData({
                    completedIntervals: intervals.map((item, index) =>
                      index === intervalIndex ? { ...item, completedSecondaryTime: nonNegativeNumber(event.target.value) } : item,
                    ),
                  })
                }
              />
            )}
            <IconButton
              aria-label={`Remove interval ${intervalIndex + 1}`}
              onClick={() => updateCompletionData({ completedIntervals: intervals.filter((_, index) => index !== intervalIndex) })}
            >
              <DeleteOutline />
            </IconButton>
          </Stack>
        ))}
        <Button
          startIcon={<Add />}
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => updateCompletionData({ completedIntervals: [...intervals, { completedPrimaryTime: 0, ...(isPerSide ? { completedSecondaryTime: 0 } : {}) }] })}
        >
          Add interval
        </Button>
        <WeightFields exercise={props.exercise} onChange={props.onChange} isPerSide={isPerSide} />
      </Stack>
    );
  }

  const unit = completionData.executionType === CompletionDataExecutionTypeEnum.Reps ? 'Repetitions' : 'Duration (seconds)';
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: isPerSide ? 6 : 12 }}>
          <TextField
            label={isPerSide ? `${unit} first side` : unit}
            type="number"
            fullWidth
            slotProps={{ htmlInput: { min: 0 } }}
            value={completionData.completedPrimaryValue ?? 0}
            onChange={(event) => updateCompletionData({ completedPrimaryValue: nonNegativeNumber(event.target.value) })}
          />
        </Grid>
        {isPerSide && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label={`${unit} second side`}
              type="number"
              fullWidth
              slotProps={{ htmlInput: { min: 0 } }}
              value={completionData.completedSecondaryValue ?? 0}
              onChange={(event) => updateCompletionData({ completedSecondaryValue: nonNegativeNumber(event.target.value) })}
            />
          </Grid>
        )}
      </Grid>
      <WeightFields exercise={props.exercise} onChange={props.onChange} isPerSide={isPerSide} />
    </Stack>
  );
};

const WeightFields = (props: { exercise: CompletedExercise; onChange: (exercise: CompletedExercise) => void; isPerSide: boolean }) => {
  const completionData = props.exercise.completionData;
  const updateCompletionData = (changes: Partial<typeof completionData>) => props.onChange({ ...props.exercise, completionData: { ...completionData, ...changes } });

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: props.isPerSide ? 6 : 12 }}>
        <TextField
          label={props.isPerSide ? 'Weight first side' : 'Weight'}
          type="number"
          fullWidth
          slotProps={{ htmlInput: { min: 0 } }}
          value={completionData.completedPrimaryWeight ?? 0}
          onChange={(event) => updateCompletionData({ completedPrimaryWeight: nonNegativeNumber(event.target.value) })}
        />
      </Grid>
      {props.isPerSide && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Weight second side"
            type="number"
            fullWidth
            slotProps={{ htmlInput: { min: 0 } }}
            value={completionData.completedSecondaryWeight ?? 0}
            onChange={(event) => updateCompletionData({ completedSecondaryWeight: nonNegativeNumber(event.target.value) })}
          />
        </Grid>
      )}
    </Grid>
  );
};

const executionTypeLabel = (executionType: CompletedExercise['completionData']['executionType']) => {
  if (executionType === CompletionDataExecutionTypeEnum.Reps) return 'Repetitions';
  if (executionType === CompletionDataExecutionTypeEnum.Duration) return 'Duration';
  return 'Intervals';
};
